import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { PRODUCTS, FAMILIES } from '../data/products';
import { Reveal } from '../components/Reveal';

const TABS = [
  { id: 'all',     label: 'All Models' },
  { id: 'capsule', label: 'Capsule Suites' },
  { id: 'studio',  label: 'Studio Suites' },
];

function ProductCard({ p, index }) {
  return (
    <Link
      to={`/products/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group block"
    >
      {/* Image stage */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F5F5F5] rounded-none">
        <img
          src={p.hero}
          alt={p.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-[1.05]"
        />
        {/* Bottom shade on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        {/* Corner markers */}
        <div className="absolute top-5 left-5 md:top-6 md:left-6 flex items-center gap-3">
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/85 mix-blend-difference">/{String(index + 1).padStart(2, '0')}</span>
          <span className="h-px w-8 bg-white/50 mix-blend-difference" />
        </div>
        <div className="absolute top-5 right-5 md:top-6 md:right-6 text-[10px] tracking-[0.28em] uppercase text-white/85 mix-blend-difference">
          {FAMILIES[p.family].name}
        </div>
        {/* Slide-in spec strip */}
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex justify-between text-white text-[10.5px] tracking-[0.24em] uppercase font-mono-luxe translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-700 ease-out">
          <span>L · {p.specs.length}</span>
          <span className="hidden sm:inline">W · {p.specs.width}</span>
          <span className="hidden sm:inline">H · {p.specs.height}</span>
          <span>{p.specs.carpetArea}</span>
        </div>
        {/* Corner ‘discover’ tag */}
        <div className="absolute bottom-5 right-5 md:bottom-6 md:right-6 opacity-0 group-hover:opacity-0 transition-opacity duration-500" />
      </div>

      {/* Product-page-style caption */}
      <div className="mt-8 md:mt-10 flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Model · {String(index + 1).padStart(2, '0')}</div>
          <div className="font-display text-6xl md:text-7xl lg:text-8xl mt-3 tracking-tight leading-[0.9]">{p.name}</div>
          <div className="mt-4 italic text-black/55 text-sm md:text-[15px] max-w-md">{p.tagline}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] tracking-[0.28em] uppercase text-black/45">Carpet Area</div>
          <div className="mt-2 font-mono-luxe text-2xl md:text-3xl">{p.specs.carpetArea}</div>
          <div className="mt-6 inline-flex items-center gap-1 text-[11px] tracking-[0.24em] uppercase group-hover:text-[#C7A36A] transition-colors">
            Discover <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>

      {/* Quiet spec ticker below caption */}
      <div className="mt-6 grid grid-cols-3 gap-4 border-t border-black/10 pt-4 text-[11px] tracking-[0.18em] uppercase text-black/55 font-mono-luxe">
        <div><span className="text-black/35">L</span> {p.specs.length}</div>
        <div><span className="text-black/35">W</span> {p.specs.width}</div>
        <div><span className="text-black/35">H</span> {p.specs.height}</div>
      </div>
    </Link>
  );
}

export default function Products() {
  const [filter, setFilter] = useState('all');
  const items = filter === 'all' ? PRODUCTS : PRODUCTS.filter((p) => p.family === filter);

  return (
    <main data-testid="products-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Products | MODULAROOM</title>
        <meta name="description" content="Explore our seven signature modular home models. Capsule Suites for iconic architecture and Studio Suites for prefabricated homes, modular resort rooms and modular offices." />
        <link rel="canonical" href="https://modularoom.com/products" />
      </Helmet>

      {/* Editorial intro */}
      <section className="pt-40 md:pt-56 pb-16 md:pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-black/45">
            <span className="text-[#C7A36A]">01</span>
            <span className="w-10 border-t border-black/20" />
            <span>The Collection</span>
          </div>
          <h1 className="mt-14 font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
            Seven signature products.<br/>
            <span className="italic text-black/40">One design language.</span>
          </h1>
          <p className="mt-10 max-w-xl text-base md:text-[17px] text-black/60 leading-[1.75]">
            Every MODULAROOM is engineered with the same commitment to precision, materials and craft. Our two families differ not in how they are built, but in the experiences they create.
          </p>

          {/* Filter tabs */}
          <div className="mt-14 flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`filter-${t.id}`}
                onClick={() => setFilter(t.id)}
                className={`rounded-full px-6 py-3 text-[12px] tracking-[0.2em] uppercase transition-colors duration-500 border ${
                  filter === t.id
                    ? 'bg-[#111] text-white border-[#111]'
                    : 'bg-transparent text-[#111] border-black/15 hover:border-[#111]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product grid — luxury showcase */}
      <section className="pb-48 md:pb-64">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <motion.div
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 md:gap-x-16 gap-y-24 md:gap-y-32"
          >
            {items.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <ProductCard p={p} index={i} />
              </Reveal>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Closing line — reinforce "products for living" */}
      <section className="bg-[#F5F5F5] py-32 md:py-48">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Not a building. A product.</div>
            <h2 className="mt-8 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              Every model is a complete architectural product.<br/>
              <span className="italic text-black/45">Delivered, not constructed.</span>
            </h2>
          </div>
          <Link to="/contact#book" className="md:col-span-4 md:justify-self-end inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-8 py-4 text-[13px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] transition-colors duration-500 self-start">
            Book Consultation <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
